import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import {
  Monitor, Smartphone, Cpu, Headphones, Car, Package,
  Plus, Search, Edit2, Trash2, X, UserCheck, RotateCcw,
  History, AlertTriangle, CheckCircle, Clock, Wrench
} from "lucide-react";
import { APIConstant } from "services/api-constant";
import * as XLSX from "xlsx";
import styles from "../hrms.module.scss";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";

const COND = ["Excellent","Good","Fair","Poor"];

const EMPTY_ASSET = {
  name:"", asset_type:"Laptop", brand:"", model:"",
  serial_number:"", purchase_date:"", purchase_value:"",
  location:"", condition:"Good", notes:"", status:"Available",
};

const EMPTY_ASSIGN = {
  employee_id:"", assigned_date: new Date().toISOString().slice(0,10),
  notes:"", condition_at_assignment:"Good",
};

// ── Auth header — every request must carry this so the backend can
// scope assets to the logged-in company. Without it, requests were
// hitting the API unauthenticated and returning unscoped/global data,
// which is why assets from every company were showing up together.
const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const iget = (url) => axios.get(url, ax()).then(r => r.data).catch(() => []);

export default function AssetInventory() {
  const { T } = useTheme();
  const ASSET_TYPES = [
    { value:"Laptop",    icon:Cpu,         color:T.cyan   },
    { value:"Desktop",   icon:Monitor,     color:T.purple },
    { value:"Mobile",    icon:Smartphone,  color:T.green  },
    { value:"Headset",   icon:Headphones,  color:T.amber  },
    { value:"Vehicle",   icon:Car,         color:T.red    },
    { value:"Other",     icon:Package,     color:T.muted  },
  ];

  const STATUS_MAP = {
    Available:     { color:T.green,  bg:`${T.green}1A`,  icon:CheckCircle  },
    Assigned:      { color:T.cyan,   bg:`${T.cyan}1A`,    icon:UserCheck    },
    "Under Repair":{ color:T.amber,  bg:`${T.amber}1A`,   icon:Wrench       },
    Retired:       { color:T.muted,  bg:T.toggle_bg,      icon:AlertTriangle},
    Lost:          { color:T.red,    bg:`${T.red}1A`,     icon:AlertTriangle},
  };

  const [assets,      setAssets]      = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [history,     setHistory]     = useState([]);
  const [stats,       setStats]       = useState({});
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [typeFilter,  setTypeFilter]  = useState("");
  const [statFilter,  setStatFilter]  = useState("");
  const [tab,         setTab]         = useState("assets"); // assets | assigned

  /* modals */
  const [assetModal,  setAssetModal]  = useState(false);
  const [mode,        setMode]        = useState("add");
  const [selected,    setSelected]    = useState(null);
  const [assignModal, setAssignModal] = useState(false);
  const [assignAsset, setAssignAsset] = useState(null);
  const [returnModal, setReturnModal] = useState(false);
  const [returnTarget,setReturnTarget]= useState(null);
  const [histModal,   setHistModal]   = useState(false);
  const [histAsset,   setHistAsset]   = useState(null);
  const [delOpen,     setDelOpen]     = useState(false);
  const [delId,       setDelId]       = useState(null);

  /* forms */
  const [form,        setForm]        = useState(EMPTY_ASSET);
  const [assignForm,  setAssignForm]  = useState(EMPTY_ASSIGN);
  const [returnForm,  setReturnForm]  = useState({ return_date: new Date().toISOString().slice(0,10), condition_at_return:"Good", notes:"" });
  const [saving,      setSaving]      = useState(false);
  const [err,         setErr]         = useState("");

  const sf = (k,v) => setForm(p => ({...p,[k]:v}));
  const saf = (k,v) => setAssignForm(p => ({...p,[k]:v}));
  const srf = (k,v) => setReturnForm(p => ({...p,[k]:v}));

  const fetchAll = async () => {
    setLoading(true);
    const [a, e, s] = await Promise.all([
      iget(APIConstant.assets),
      iget(APIConstant.employees),
      iget(APIConstant.assetStats),
    ]);
    setAssets(Array.isArray(a) ? a : a.data || []);
    setEmployees(Array.isArray(e) ? e : e.data || []);
    setStats(s || {});
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchHistory = async (assetId) => {
    const h = await iget(`${APIConstant.assets}${assetId}/history`);
    setHistory(Array.isArray(h) ? h : h.data || []);
  };

  /* ── filtered list ── */
  const filtered = useMemo(() => assets.filter(a => {
    const s = search.toLowerCase();
    const ms = !search || `${a.name} ${a.asset_code} ${a.serial_number} ${a.brand}`.toLowerCase().includes(s);
    const mt = !typeFilter || a.asset_type === typeFilter;
    const mst= !statFilter || a.status === statFilter;
    return ms && mt && mst;
  }), [assets, search, typeFilter, statFilter]);

  /* assigned tab */
  const assigned = useMemo(() => assets.filter(a => a.status === "Assigned"), [assets]);

  /* ── CRUD handlers ── */
  const openAdd  = () => { setMode("add"); setForm(EMPTY_ASSET); setErr(""); setAssetModal(true); };
  const openEdit = (a) => { setMode("edit"); setSelected(a); setForm({...EMPTY_ASSET,...a}); setErr(""); setAssetModal(true); };

  const saveAsset = async () => {
    if (!form.name) return setErr("Asset name is required");
    if (!form.serial_number) return setErr("Serial number is required");
    setSaving(true); setErr("");
    try {
      const payload = { ...form, purchase_value: form.purchase_value ? parseFloat(form.purchase_value) : null };
      if (mode === "add") await axios.post(APIConstant.assets || "/assets/", payload, ax());
      else                await axios.put(`${APIConstant.assets}${selected.id}`, payload, ax());
      setAssetModal(false); fetchAll();
    } catch(e) { setErr(e.response?.data?.detail || "Error saving asset"); }
    finally { setSaving(false); }
  };

  const deleteAsset = async () => {
    try { await axios.delete(`${APIConstant.assets}${delId}`, ax()); setDelOpen(false); fetchAll(); }
    catch(e) { console.error(e); }
  };

  const openAssign = (a) => { setAssignAsset(a); setAssignForm(EMPTY_ASSIGN); setErr(""); setAssignModal(true); };
  const saveAssign = async () => {
    if (!assignForm.employee_id) return setErr("Select an employee");
    setSaving(true); setErr("");
    try {
      await axios.post(`${APIConstant.assets}${assignAsset.id}/assign`, assignForm, ax());
      setAssignModal(false); fetchAll();
    } catch(e) { setErr(e.response?.data?.detail || "Error assigning asset"); }
    finally { setSaving(false); }
  };

  const openReturn = (a) => { setReturnTarget(a); setReturnForm({ return_date: new Date().toISOString().slice(0,10), condition_at_return:"Good", notes:"" }); setReturnModal(true); };
  const saveReturn = async () => {
    setSaving(true);
    try {
      await axios.post(`${APIConstant.assets}${returnTarget.id}/return`, returnForm, ax());
      setReturnModal(false); fetchAll();
    } catch(e) { console.error(e); }
    finally { setSaving(false); }
  };

  const openHistory = async (a) => {
    setHistAsset(a); await fetchHistory(a.id); setHistModal(true);
  };

  /* ── helpers ── */
  const typeInfo = (t) => ASSET_TYPES.find(x => x.value === t) || ASSET_TYPES[ASSET_TYPES.length-1];
  const empName  = (id) => { const e = employees.find(x => x.id === parseInt(id)); return e ? `${e.first_name} ${e.last_name}` : "—"; };

  /* ── Excel Download ── */
  const downloadExcel = () => {
    const rows = (tab === "assets" ? filtered : assigned).map(a => ({
      "Asset Code":      a.asset_code      || "",
      "Name":            a.name            || "",
      "Type":            a.asset_type      || "",
      "Brand":           a.brand           || "",
      "Model":           a.model           || "",
      "Serial Number":   a.serial_number   || "",
      "Status":          a.status          || "",
      "Condition":       a.condition       || "",
      "Location":        a.location        || "",
      "Purchase Date":   a.purchase_date   || "",
      "Purchase Value":  a.purchase_value  ? `₹${Number(a.purchase_value).toLocaleString()}` : "",
      "Assigned To":     a.assigned_to_name || (a.assigned_to_id ? empName(a.assigned_to_id) : ""),
      "Assigned Date":   a.assigned_date   || "",
      "Notes":           a.notes           || "",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);

    // Column widths
    ws["!cols"] = [
      {wch:12},{wch:24},{wch:12},{wch:14},{wch:16},{wch:22},
      {wch:14},{wch:12},{wch:20},{wch:14},{wch:16},{wch:22},{wch:14},{wch:28},
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Assets");
    const date = new Date().toISOString().slice(0,10);
    XLSX.writeFile(wb, `Hirality_Assets_${date}.xlsx`);
  };

  const StatusBadge = ({ s }) => {
    const m = STATUS_MAP[s] || STATUS_MAP["Available"];
    const Icon = m.icon;
    return (
      <span style={{ display:"inline-flex", alignItems:"center", gap:4,
        padding:"3px 10px", borderRadius:100, fontSize:11, fontWeight:700,
        background:m.bg, color:m.color }}>
        <Icon size={10}/>{s}
      </span>
    );
  };

  /* ── INPUT STYLE ── */
  const inp = (extra={}) => ({
    width:"100%", background:T.inp_bg,
    border:`1px solid ${T.border}`, borderRadius:8,
    color:T.text, fontSize:13, padding:"9px 12px",
    outline:"none", fontFamily:"Outfit,sans-serif",
    boxSizing:"border-box", ...extra,
  });
  const lbl = { fontSize:11, color:T.muted, display:"block",
    marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" };
  const fg = { marginBottom:14 };

  /* ══ RENDER ══ */
  return (
    <div className={styles.pageRoot}>

      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>ASSET MANAGEMENT</div>
          <h2>Asset Inventory</h2>
          <p>Track company assets assigned to employees</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnPrimary} onClick={openAdd}>
            <Plus size={14}/> Add Asset
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className={`${styles.statsGrid} ${styles.cols4}`}>
        {[
          { label:"Total Assets",   val: stats.total        || assets.length,                              color:"cyan"   },
          { label:"Assigned",       val: stats.assigned     || assets.filter(a=>a.status==="Assigned").length, color:"purple" },
          { label:"Available",      val: stats.available    || assets.filter(a=>a.status==="Available").length,color:"green"  },
          { label:"Under Repair",   val: stats.under_repair || assets.filter(a=>a.status==="Under Repair").length,color:"amber"},
        ].map(({label,val,color}) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:"flex", gap:4, marginBottom:16 }}>
        {[["assets","All Assets"],["assigned","Currently Assigned"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding:"8px 18px", borderRadius:8, fontSize:13, fontWeight:600,
            cursor:"pointer", border:"none", fontFamily:"Outfit,sans-serif",
            background: tab===k ? `linear-gradient(135deg,${T.cyan}26,${T.purple}26)` : "transparent",
            color: tab===k ? T.cyan : T.muted,
            borderBottom: tab===k ? `2px solid ${T.cyan}` : "2px solid transparent",
          }}>{l}</button>
        ))}
      </div>

      {/* ── Main Table Card ── */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <h3 style={{margin:0}}>
              {tab==="assets" ? "All Assets" : "Assigned Assets"}
              <span style={{fontSize:12,color:T.muted,fontWeight:400,marginLeft:6}}>
                ({tab==="assets" ? filtered.length : assigned.length})
              </span>
            </h3>
            <button onClick={downloadExcel}
              disabled={(tab==="assets"?filtered:assigned).length===0}
              style={{
                display:"flex",alignItems:"center",gap:6,
                padding:"7px 14px",borderRadius:8,cursor:"pointer",
                border:`1px solid ${T.green}4D`,
                background:`${T.green}12`,
                color:T.green,fontSize:12,fontWeight:700,
                fontFamily:"Outfit,sans-serif",
                opacity:(tab==="assets"?filtered:assigned).length===0?0.4:1,
              }}>
              ↓ Export Excel
            </button>
          </div>
          {tab==="assets" && (
            <div style={{display:"flex",gap:10}}>
              <div style={{position:"relative"}}>
                <Search size={13} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.muted}}/>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search assets…"
                  style={{...inp({paddingLeft:30,width:200})}}/>
              </div>
              <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)} style={inp({width:"auto"})}>
                <option value="">All Types</option>
                {ASSET_TYPES.map(t=><option key={t.value} style={{background:T.opt_bg}}>{t.value}</option>)}
              </select>
              <select value={statFilter} onChange={e=>setStatFilter(e.target.value)} style={inp({width:"auto"})}>
                <option value="">All Status</option>
                {Object.keys(STATUS_MAP).map(s=><option key={s} style={{background:T.opt_bg}}>{s}</option>)}
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead>
                <tr>
                  <th>Asset</th><th>Code</th><th>Type</th>
                  <th>Serial No.</th><th>Condition</th>
                  {tab==="assigned" && <th>Assigned To</th>}
                  {tab==="assigned" && <th>Since</th>}
                  <th>Location</th><th>Value</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(tab==="assets" ? filtered : assigned).length === 0 ? (
                  <tr className={styles.emptyRow}><td colSpan={11}>No assets found</td></tr>
                ) : (tab==="assets" ? filtered : assigned).map(a => {
                  const ti = typeInfo(a.asset_type);
                  const Icon = ti.icon;
                  return (
                    <tr key={a.id}>
                      <td>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:34,height:34,borderRadius:9,
                            background:`${ti.color}1F`,
                            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            <Icon size={16} style={{color:ti.color}}/>
                          </div>
                          <div>
                            <div style={{fontWeight:600,fontSize:13}}>{a.name}</div>
                            <div style={{fontSize:11,color:T.muted}}>{a.brand} {a.model}</div>
                          </div>
                        </div>
                      </td>
                      <td><span style={{fontFamily:"JetBrains Mono,monospace",fontSize:12,color:T.cyan}}>{a.asset_code||"—"}</span></td>
                      <td><span style={{fontSize:12,color:ti.color}}>{a.asset_type}</span></td>
                      <td><span style={{fontFamily:"monospace",fontSize:12,color:T.muted}}>{a.serial_number||"—"}</span></td>
                      <td>
                        <span style={{fontSize:12,color:a.condition==="Excellent"||a.condition==="Good"?T.green:a.condition==="Fair"?T.amber:T.red}}>
                          {a.condition||"—"}
                        </span>
                      </td>
                      {tab==="assigned" && <td style={{fontSize:13}}>{empName(a.assigned_to_id) || a.assigned_to_name || "—"}</td>}
                      {tab==="assigned" && <td style={{fontSize:12,color:T.muted}}>{a.assigned_date||"—"}</td>}
                      <td style={{fontSize:12,color:T.muted}}>{a.location||"—"}</td>
                      <td style={{fontSize:12,color:T.text}}>{a.purchase_value ? `₹${Number(a.purchase_value).toLocaleString()}` : "—"}</td>
                      <td><StatusBadge s={a.status}/></td>
                      <td>
                        <div style={{display:"flex",gap:5}}>
                          {a.status==="Available" && (
                            <button className={styles.btnSm} style={{color:T.green,borderColor:`${T.green}40`}}
                              onClick={()=>openAssign(a)} title="Assign to employee">
                              <UserCheck size={12}/>
                            </button>
                          )}
                          {a.status==="Assigned" && (
                            <button className={styles.btnSm} style={{color:T.amber,borderColor:`${T.amber}40`}}
                              onClick={()=>openReturn(a)} title="Return asset">
                              <RotateCcw size={12}/>
                            </button>
                          )}
                          <button className={styles.btnSm} onClick={()=>openHistory(a)} title="View history">
                            <History size={12}/>
                          </button>
                          <button className={styles.btnSm} onClick={()=>openEdit(a)} title="Edit">
                            <Edit2 size={12}/>
                          </button>
                          <button className={styles.btnSm} style={{color:T.red,borderColor:`${T.red}40`}}
                            onClick={()=>{setDelId(a.id);setDelOpen(true);}} title="Delete">
                            <Trash2 size={12}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══ ADD / EDIT ASSET MODAL ══ */}
      <Dialog open={assetModal} onClose={()=>setAssetModal(false)} maxWidth="md">
        <div style={{background:T.card,border:`1px solid ${T.cyan}33`,borderRadius:14,
          width:640,overflow:"hidden",fontFamily:"Outfit,sans-serif"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,
            background:`${T.cyan}0A`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h3 style={{margin:0,fontSize:16,fontWeight:700,color:T.text}}>
              {mode==="add" ? "Add New Asset" : "Edit Asset"}
            </h3>
            <button style={{background:"none",border:"none",cursor:"pointer",color:T.muted}} onClick={()=>setAssetModal(false)}><X size={16}/></button>
          </div>
          <div style={{padding:24,maxHeight:"72vh",overflowY:"auto"}}>

            {/* Asset type selector */}
            <div style={fg}>
              <label style={lbl}>Asset Type</label>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {ASSET_TYPES.map(t => {
                  const Icon = t.icon;
                  const sel  = form.asset_type === t.value;
                  return (
                    <button key={t.value} type="button" onClick={() => sf("asset_type", t.value)}
                      style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",
                        borderRadius:8,border:`1px solid ${sel?t.color:T.border}`,cursor:"pointer",
                        background:sel?`${t.color}18`:"transparent",color:sel?t.color:T.muted,
                        fontFamily:"Outfit,sans-serif",fontSize:12,fontWeight:600}}>
                      <Icon size={13}/>{t.value}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
              {[
                {k:"name",          l:"Asset Name *",    pl:"e.g. MacBook Pro 14"},
                {k:"brand",         l:"Brand",           pl:"e.g. Apple"},
                {k:"model",         l:"Model",           pl:"e.g. MNW83HN/A"},
                {k:"serial_number", l:"Serial Number *", pl:"e.g. C02XG2JYJG5H"},
              ].map(({k,l,pl}) => (
                <div key={k} style={fg}>
                  <label style={lbl}>{l}</label>
                  <input value={form[k]||""} onChange={e=>sf(k,e.target.value)}
                    placeholder={pl} style={inp()}/>
                </div>
              ))}

              <div style={fg}>
                <label style={lbl}>Purchase Date</label>
                <input type="date" value={form.purchase_date||""} onChange={e=>sf("purchase_date",e.target.value)} style={inp()}/>
              </div>
              <div style={fg}>
                <label style={lbl}>Purchase Value (₹)</label>
                <input type="number" value={form.purchase_value||""} onChange={e=>sf("purchase_value",e.target.value)}
                  placeholder="e.g. 85000" style={inp()}/>
              </div>
              <div style={fg}>
                <label style={lbl}>Condition</label>
                <select value={form.condition||"Good"} onChange={e=>sf("condition",e.target.value)} style={inp()}>
                  {COND.map(c=><option key={c} style={{background:T.opt_bg}}>{c}</option>)}
                </select>
              </div>
              <div style={fg}>
                <label style={lbl}>Status</label>
                <select value={form.status||"Available"} onChange={e=>sf("status",e.target.value)} style={inp()}>
                  {Object.keys(STATUS_MAP).map(s=><option key={s} style={{background:T.opt_bg}}>{s}</option>)}
                </select>
              </div>
              <div style={{...fg,gridColumn:"1/-1"}}>
                <label style={lbl}>Location</label>
                <input value={form.location||""} onChange={e=>sf("location",e.target.value)}
                  placeholder="e.g. Head Office – Floor 2" style={inp()}/>
              </div>
              <div style={{...fg,gridColumn:"1/-1"}}>
                <label style={lbl}>Notes</label>
                <textarea rows={2} value={form.notes||""} onChange={e=>sf("notes",e.target.value)}
                  placeholder="Any additional notes…" style={{...inp(),resize:"vertical"}}/>
              </div>
            </div>

            {err && <div style={{padding:"9px 14px",borderRadius:8,background:`${T.red}1A`,
              border:`1px solid ${T.red}40`,color:T.red,fontSize:13,marginBottom:12}}>⚠️ {err}</div>}

            <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:12,borderTop:`1px solid ${T.border}`}}>
              <button onClick={()=>setAssetModal(false)} className={styles.btnSecondary}>Cancel</button>
              <button onClick={saveAsset} disabled={saving} className={styles.btnPrimary}>
                {saving ? "Saving…" : mode==="add" ? "Add Asset" : "Update Asset"}
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* ══ ASSIGN MODAL ══ */}
      <Dialog open={assignModal} onClose={()=>setAssignModal(false)}>
        <div style={{background:T.card,border:`1px solid ${T.green}40`,borderRadius:14,
          width:460,fontFamily:"Outfit,sans-serif",overflow:"hidden"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,
            background:`${T.green}0A`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <h3 style={{margin:"0 0 2px",fontSize:15,fontWeight:700,color:T.green}}>
                <UserCheck size={14} style={{marginRight:6,verticalAlign:"middle"}}/>Assign Asset
              </h3>
              <div style={{fontSize:12,color:T.muted}}>{assignAsset?.name} · {assignAsset?.asset_code}</div>
            </div>
            <button style={{background:"none",border:"none",cursor:"pointer",color:T.muted}} onClick={()=>setAssignModal(false)}><X size={16}/></button>
          </div>
          <div style={{padding:24}}>
            <div style={fg}>
              <label style={lbl}>Assign To *</label>
              <select value={assignForm.employee_id} onChange={e=>saf("employee_id",e.target.value)} style={inp()}>
                <option value="">— Select Employee —</option>
                {employees.map(e=>(
                  <option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.employee_code||e.email}</option>
                ))}
              </select>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div style={fg}>
                <label style={lbl}>Assignment Date</label>
                <input type="date" value={assignForm.assigned_date} onChange={e=>saf("assigned_date",e.target.value)} style={inp()}/>
              </div>
              <div style={fg}>
                <label style={lbl}>Condition at Handover</label>
                <select value={assignForm.condition_at_assignment} onChange={e=>saf("condition_at_assignment",e.target.value)} style={inp()}>
                  {COND.map(c=><option key={c} style={{background:T.opt_bg}}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={fg}>
              <label style={lbl}>Notes</label>
              <textarea rows={2} value={assignForm.notes} onChange={e=>saf("notes",e.target.value)}
                placeholder="Handover notes…" style={{...inp(),resize:"vertical"}}/>
            </div>
            {err && <div style={{padding:"9px 14px",borderRadius:8,background:`${T.red}1A`,
              border:`1px solid ${T.red}40`,color:T.red,fontSize:13,marginBottom:12}}>⚠️ {err}</div>}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:12,borderTop:`1px solid ${T.border}`}}>
              <button onClick={()=>setAssignModal(false)} className={styles.btnSecondary}>Cancel</button>
              <button onClick={saveAssign} disabled={saving}
                style={{padding:"9px 22px",borderRadius:8,border:"none",
                  background:`linear-gradient(135deg,${T.green},#00B386)`,
                  color:"#fff",fontWeight:800,fontSize:13,cursor:"pointer",fontFamily:"Outfit,sans-serif"}}>
                {saving ? "Assigning…" : "Assign Asset"}
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* ══ RETURN MODAL ══ */}
      <Dialog open={returnModal} onClose={()=>setReturnModal(false)}>
        <div style={{background:T.card,border:`1px solid ${T.amber}40`,borderRadius:14,
          width:420,fontFamily:"Outfit,sans-serif",overflow:"hidden"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,
            background:`${T.amber}0A`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <h3 style={{margin:"0 0 2px",fontSize:15,fontWeight:700,color:T.amber}}>
                <RotateCcw size={14} style={{marginRight:6,verticalAlign:"middle"}}/>Return Asset
              </h3>
              <div style={{fontSize:12,color:T.muted}}>{returnTarget?.name} · assigned to {empName(returnTarget?.assigned_to_id)}</div>
            </div>
            <button style={{background:"none",border:"none",cursor:"pointer",color:T.muted}} onClick={()=>setReturnModal(false)}><X size={16}/></button>
          </div>
          <div style={{padding:24}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div style={fg}>
                <label style={lbl}>Return Date</label>
                <input type="date" value={returnForm.return_date} onChange={e=>srf("return_date",e.target.value)} style={inp()}/>
              </div>
              <div style={fg}>
                <label style={lbl}>Condition on Return</label>
                <select value={returnForm.condition_at_return} onChange={e=>srf("condition_at_return",e.target.value)} style={inp()}>
                  {COND.map(c=><option key={c} style={{background:T.opt_bg}}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={fg}>
              <label style={lbl}>Return Notes</label>
              <textarea rows={2} value={returnForm.notes} onChange={e=>srf("notes",e.target.value)}
                placeholder="Condition remarks, damage notes…" style={{...inp(),resize:"vertical"}}/>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:12,borderTop:`1px solid ${T.border}`}}>
              <button onClick={()=>setReturnModal(false)} className={styles.btnSecondary}>Cancel</button>
              <button onClick={saveReturn} disabled={saving}
                style={{padding:"9px 22px",borderRadius:8,border:"none",
                  background:`linear-gradient(135deg,${T.amber},#E67E00)`,
                  color:"#fff",fontWeight:800,fontSize:13,cursor:"pointer",fontFamily:"Outfit,sans-serif"}}>
                {saving ? "Processing…" : "Confirm Return"}
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* ══ HISTORY MODAL ══ */}
      <Dialog open={histModal} onClose={()=>setHistModal(false)} maxWidth="sm">
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,
          width:520,fontFamily:"Outfit,sans-serif",overflow:"hidden"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,
            display:"flex",justifyContent:"space-between",alignItems:"center",
            background:`${T.purple}0F`}}>
            <div>
              <h3 style={{margin:"0 0 2px",fontSize:15,fontWeight:700,color:T.purple}}>
                <History size={14} style={{marginRight:6,verticalAlign:"middle"}}/>Assignment History
              </h3>
              <div style={{fontSize:12,color:T.muted}}>{histAsset?.name} · {histAsset?.asset_code}</div>
            </div>
            <button style={{background:"none",border:"none",cursor:"pointer",color:T.muted}} onClick={()=>setHistModal(false)}><X size={16}/></button>
          </div>
          <div style={{maxHeight:400,overflowY:"auto",padding:20}}>
            {history.length === 0 ? (
              <div style={{textAlign:"center",padding:"32px 0",color:T.muted,fontSize:13}}>
                <Clock size={32} style={{opacity:.3,marginBottom:8,display:"block",margin:"0 auto 8px"}}/>
                No assignment history yet
              </div>
            ) : history.map((h,i) => (
              <div key={i} style={{
                padding:"14px 16px",borderRadius:10,marginBottom:8,
                background:h.is_active?`${T.cyan}0D`:T.toggle_bg,
                border:`1px solid ${h.is_active?T.border:T.toggle_bg}`,
              }}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div style={{fontWeight:600,fontSize:13}}>{empName(h.employee_id) || h.employee_name}</div>
                  {h.is_active
                    ? <span style={{fontSize:10,padding:"2px 8px",borderRadius:100,background:`${T.cyan}1A`,color:T.cyan,fontWeight:700}}>Current</span>
                    : <span style={{fontSize:10,padding:"2px 8px",borderRadius:100,background:T.toggle_bg,color:T.muted,fontWeight:700}}>Returned</span>
                  }
                </div>
                <div style={{display:"flex",gap:16,fontSize:12,color:T.muted}}>
                  <span>📅 Assigned: {h.assigned_date||"—"}</span>
                  {h.return_date && <span>↩ Returned: {h.return_date}</span>}
                </div>
                {(h.condition_at_assignment || h.condition_at_return) && (
                  <div style={{display:"flex",gap:16,fontSize:11,color:T.muted,marginTop:4}}>
                    {h.condition_at_assignment && <span>Out: <strong style={{color:T.text}}>{h.condition_at_assignment}</strong></span>}
                    {h.condition_at_return    && <span>In:  <strong style={{color:T.text}}>{h.condition_at_return}</strong></span>}
                  </div>
                )}
                {h.notes && <div style={{fontSize:12,color:T.muted,marginTop:6,fontStyle:"italic"}}>"{h.notes}"</div>}
              </div>
            ))}
          </div>
        </div>
      </Dialog>

      <Popup open={delOpen} message="Delete this asset? All assignment history will also be removed."
        handleClose={()=>setDelOpen(false)} handleConfirm={deleteAsset}/>
    </div>
  );
}